import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import styles from './ScheduleInterviewModal.module.css';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import TimePicker from '../../components/TimePicker/TimePicker';
import ErrorModal from '../../components/Modals/ErrorModal';
import { selectUserRole, selectOutlets } from '../../store/selectors/authSelectors';
import { InterviewItem, InterviewFormData } from './types';

interface ScheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate?: InterviewItem | null;
  candidates?: InterviewItem[] | null;
  onSchedule: (candidateId: string, details: InterviewFormData) => void;
  onBulkSchedule: (details: InterviewFormData) => void;
  onEditInterview: (candidateId: string, details: InterviewFormData) => void;
  isEditMode?: boolean;
  isSubmitting?: boolean;
}

const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({
  isOpen,
  onClose,
  candidate,
  candidates,
  onSchedule,
  onBulkSchedule,
  onEditInterview,
  isEditMode = false,
  isSubmitting,
}) => {
  // Initialize with default time 09:00 (matching TimePicker default)
  const [details, setDetails] = useState<InterviewFormData>({ date: '', time: '09:00', outlet_id: '' });
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });
  const userRole = useSelector(selectUserRole);
  const outlets = useSelector(selectOutlets);
  const isBulkMode = !!candidates;

  // Auto-populate outlet_id for OUTLET_MANAGER and OUTLET_POC in create mode
  useEffect(() => {
    if (!isOpen || !userRole || !outlets || outlets.length === 0 || isEditMode) {
      return;
    }

    // Auto-populate outlet_id for OUTLET_MANAGER and OUTLET_POC in create mode
    if ((userRole === 'OUTLET_MANAGER' || userRole === 'OUTLET_POC') && outlets.length > 0) {
      setDetails((prev) => ({ ...prev, outlet_id: outlets[0].outlet_id }));
    }
  }, [isOpen, userRole, outlets, isEditMode]);

  // Prepopulate form when editing
  useEffect(() => {
    if (isOpen && isEditMode && candidate && candidate.interviewDetails && outlets.length > 0) {
      // For editing, match venue name to outlet_id
      let outletId = '';
      if (candidate.interviewDetails.outlet_id) {
        // If outlet_id is directly available
        outletId = candidate.interviewDetails.outlet_id;
      } else if (candidate.interviewDetails.venue) {
        // Try to match venue name to outlet
        const matchedOutlet = outlets.find(
          (outlet) => outlet.outlet_name === candidate.interviewDetails?.venue
        );
        if (matchedOutlet) {
          outletId = matchedOutlet.outlet_id;
        }
      }

      setDetails({
        date: candidate.interviewDetails.date || '',
        time: candidate.interviewDetails.time || '',
        outlet_id: outletId,
      });
    } else if (isOpen && !isEditMode) {
      // Reset form for new scheduling, but preserve outlet_id if already auto-populated
      // Set default time to 09:00 (matching TimePicker default)
      setDetails((prev) => {
        // Only reset date/time if outlet_id is already set (autopopulated)
        if (prev.outlet_id) {
          return { date: '', time: '09:00', outlet_id: prev.outlet_id };
        }
        return { date: '', time: '09:00', outlet_id: '' };
      });
    }
  }, [isOpen, isEditMode, candidate, outlets]);

  if (!isOpen) return null;

  // Get today's date in YYYY-MM-DD format for min date validation
  const today = dayjs().format('YYYY-MM-DD');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!details.date) {
      setErrorModal({ isOpen: true, message: 'Please select a date for the interview.' });
      return;
    }

    if (!details.time) {
      setErrorModal({ isOpen: true, message: 'Please select a time for the interview.' });
      return;
    }

    // Past time validation
    const currentTime = dayjs().format('HH:mm');
    if (details.date === today && details.time <= currentTime) {
      setErrorModal({ isOpen: true, message: 'The interview time must be in the future. Please select a later time.' });
      return;
    }

    // Validate outlet_id is available
    if (!details.outlet_id) {
      setErrorModal({ isOpen: true, message: 'Please select an outlet for the interview.' });
      return;
    }

    const selectedOutlet = outlets.find((o) => o.outlet_id === details.outlet_id);
    const submissionData: InterviewFormData = {
      ...details,
      venue: selectedOutlet?.outlet_name || '',
    };

    if (isEditMode && candidate) {
      onEditInterview(candidate.id, submissionData);
    } else if (isBulkMode) {
      onBulkSchedule(submissionData);
    } else if (candidate) {
      onSchedule(candidate.id, submissionData);
    }
  };

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose} data-testid="schedule-interview-overlay">
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} data-testid="ats-schedule-interview-modal">
          <form onSubmit={handleSubmit}>
            <h2 className={styles.modalTitle}>
              {isEditMode ? 'Edit Interview' : 'Schedule Interview'}
            </h2>
            {isBulkMode && candidates ? (
              <p className={styles.modalSubtitle}>For: {candidates.length} selected candidates</p>
            ) : candidate ? (
              <p className={styles.modalSubtitle}>For: {candidate.name}</p>
            ) : null}

            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel}>Date</label>
              <input
                className={styles.formGroupInput}
                type="date"
                required
                min={today}
                value={details.date}
                onChange={(e) => setDetails({ ...details, date: e.target.value })}
                disabled={isSubmitting}
                data-testid="schedule-interview-date-input"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel}>Time</label>
              <TimePicker
                value={details.time}
                onChange={(time: string) => setDetails({ ...details, time })}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel}>Outlet*</label>
              {!userRole || outlets.length === 0 ? (
                <div className={styles.loadingMessage}>Loading outlets...</div>
              ) : userRole === 'ORG_ADMIN' ? (
                <select
                  className={styles.formGroupInput}
                  value={details.outlet_id || ''}
                  onChange={(e) => setDetails({ ...details, outlet_id: e.target.value })}
                  disabled={isSubmitting}
                  required
                  data-testid="schedule-interview-outlet-select"
                >
                  <option value="">Select an outlet</option>
                  {outlets.map((outlet) => (
                    <option key={outlet.outlet_id} value={outlet.outlet_id}>
                      {outlet.outlet_name}
                    </option>
                  ))}
                </select>
              ) : (userRole === 'OUTLET_MANAGER' || userRole === 'OUTLET_POC') &&
                outlets.length > 0 ? (
                <div className={styles.formInputDisabled}>
                  <span>{outlets[0].outlet_name}</span>
                </div>
              ) : (
                <div className={styles.errorText}>Unable to load outlet information</div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.buttonSecondary}
                onClick={onClose}
                disabled={isSubmitting}
                data-testid="schedule-interview-cancel-button"
              >
                Cancel
              </button>
              <button type="submit" className={styles.buttonPrimary} disabled={isSubmitting} data-testid="schedule-interview-submit-button">
                {isSubmitting ? (
                  <LoadingSpinner size="small" />
                ) : isEditMode ? (
                  'Update Interview'
                ) : isBulkMode && candidates ? (
                  `Schedule for ${candidates.length} Candidates`
                ) : (
                  'Confirm & Schedule'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
      />
    </>
  );
};

export default ScheduleInterviewModal;
